import format from 'date-fns/format';
import Cookies from 'js-cookie';

import Location from 'services/Location';
import { productsOrder } from 'helpers/product';
import { AccountRole } from 'services/connectivity/accounts/types';
import { OrganizationGraphQLResponse } from 'hooks/use-organizations';
import { ProductAccess } from 'types/product';
import { FilteredOrganization } from 'types/organization';

export const sortProducts = (products: ProductAccess[]): ProductAccess[] =>
  products.sort((a, b) => productsOrder.indexOf(a.name) - productsOrder.indexOf(b.name));

export const getAccountOrganizationProducts = (
  organizations: OrganizationGraphQLResponse[],
  accountRoles: AccountRole[],
  isAccountWhitelisted?: boolean
): FilteredOrganization[] => {
  const filteredOrganizations = organizations?.map((organization) => {
    const orgProducts = organization.products;
    const accountProducts = orgProducts.reduce((acc, orgProduct) => {
      const haveAccessToProduct = accountRoles.some(
        (role) =>
          role.organization_id === organization.organization_id && role.product === orgProduct.name
      );

      return [
        ...acc,
        {
          name: orgProduct.name,
          haveAccessToProduct,
          canJoinProduct: haveAccessToProduct || isAccountWhitelisted,
        },
      ];
    }, []);

    const sortedProducts = sortProducts(accountProducts);

    const filteredOrganization = {
      id: organization.organization_id,
      name: organization.name,
      createdAt: format(new Date(organization.created_at), 'MMM dd, yyyy'),
      products: sortedProducts,
    };
    return filteredOrganization;
  });
  return filteredOrganizations;
};

type Props = {
  productToActivate: string;
  organization: OrganizationGraphQLResponse;
  accountRoles: AccountRole[];
  isAccountWhitelisted?: boolean;
};

export const canActivateProduct = ({
  productToActivate,
  organization,
  accountRoles,
  isAccountWhitelisted,
}: Props): boolean => {
  const matchingRoles = accountRoles.filter(
    (role) => role.organization_id === organization.organization_id
  );
  if (!isAccountWhitelisted && !matchingRoles.length) {
    return false;
  }

  const alreadyHasLC = accountRoles?.some((role) => role.product === 'LiveChat');
  const isMeOwner = accountRoles?.some(
    (role) =>
      role.organization_id === organization.organization_id &&
      role.product === 'Accounts' &&
      role.role === 'owner'
  );

  if (productToActivate === 'LiveChat' && !alreadyHasLC) {
    return (
      isAccountWhitelisted ||
      (matchingRoles.some((role) => role.role === 'owner' && role.product === 'Accounts') &&
        matchingRoles.some((role) => role.scopes.includes('organization.product--my:rw')))
    );
  }

  if (productToActivate === 'LiveChat' && alreadyHasLC) {
    return false;
  }

  if (productToActivate === 'PartnerProgram' && !isMeOwner) {
    return false;
  }

  return (
    isAccountWhitelisted ||
    matchingRoles.some((role) => role.scopes.includes('organization.product--my:rw'))
  );
};

export const accountProductsToDisplay = (
  currentOrganization: OrganizationGraphQLResponse,
  accountFilteredOrganizations: FilteredOrganization[]
): string[] | null => {
  const currentOrgDetails = accountFilteredOrganizations.find(
    (organization) => organization.id === currentOrganization.organization_id
  );
  if (currentOrgDetails) {
    return currentOrgDetails.products
      .filter(({ haveAccessToProduct }) => haveAccessToProduct)
      .map(({ name }) => name);
  }
  return null;
};

export const formatArray = (products: string[]): string => {
  if (products.length === 0) {
    return '';
  }
  if (products.length === 1) {
    return products[0];
  }
  if (products.length === 2) {
    return products.join(' and ');
  }
  return `${products.slice(0, -1).join(', ')}, and ${products.slice(-1)}`;
};

export const organizationIDEscaped = (organizationId: string): string =>
  encodeURIComponent(organizationId);

export const getAccessToken = (): string =>
  Location.getUrlParam('access_token') ||
  Location.getHashParam('access_token') ||
  Cookies.get('access_token');

export const getScopes = (): string =>
  Location.getUrlParam('scope') || Location.getHashParam('scope') || Cookies.get('scope');
